





















import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  BulletListNode,
  CommentHighlight,
  HardBreakNode,
  HeadingLevel,
  HeadingNode,
  ListItemNode,
  MergeValueNode,
  NodeAlign,
  OrderedListNode,
  ParagraphNode,
  ProsemirrorType,
  TextAlign,
  TextDocNode,
} from '@getaccept/editor-lib';
import type { TextContentDoc } from '@getaccept/lib-shared/src/types/text-content';
import { BaseIcon } from '@getaccept/lib-ui';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import type { EditorOptions } from '@tiptap/vue-2';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import type { PropType, Ref } from '@vue/composition-api';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import debounce from 'debounce';
import type { Comment } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { Transaction } from 'prosemirror-state';
import type { ProseMirrorSelection } from '@getaccept/lib-shared/src/contextual-comments/types/comment-selection';
import { SelectionType } from '@getaccept/lib-shared/src/contextual-comments/types/comment-selection';
import { handleInit, setContent, updateMergeValues } from './helpers/prosemirror-viewer.helper';

export default defineComponent({
  name: 'ProsemirrorViewerText',
  components: {
    EditorContent,
    BaseIcon,
  },
  props: {
    defaultHeading: {
      type: Number as PropType<HeadingLevel>,
      default: () => HeadingLevel.Heading1,
    },
    defaultAlign: { type: String as PropType<NodeAlign>, default: () => NodeAlign.Left },
    content: {
      type: [Object, String] as PropType<TextContentDoc | string>,
      default: '',
    },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
    comments: { type: Array as PropType<Comment[]>, default: () => [] },
    selectedCommentId: { type: String, default: '' },
    nodeId: { type: String, default: '' },
    hasContextualCommenting: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const viewer: Ref<TipTapEditor | null> = ref(null);

    const editorContainer = ref(null);

    const contentPayload = computed(() => ({
      viewer: viewer.value,
      content: props.content,
      fields: props.fields,
      mergeValues: props.mergeValues,
    }));

    const textViewer = computed<Partial<EditorOptions>>(() => ({
      content: props.content,
      editable: false,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        TextDocNode,
        ParagraphNode,
        Text,
        Bold,
        Italic,
        Strike,
        Superscript,
        Underline,
        OrderedListNode,
        ...(props.hasContextualCommenting ? [CommentHighlight] : []),
        BulletListNode,
        ListItemNode,
        MergeValueNode,
        Link.configure({
          autolink: false,
          openOnClick: true,
          linkOnPaste: false,
        }),
        HeadingNode.configure({
          defaultLevel: props.defaultHeading,
        }),
        HardBreakNode,
        TextAlign.configure({
          defaultAlignment: props.defaultAlign,
          types: [
            ProsemirrorType.Paragraph,
            ProsemirrorType.Heading,
            ProsemirrorType.BulletList,
            ProsemirrorType.OrderedList,
          ],
        }),
      ],
      onCreate: () => handleInit(viewer.value, props.fields, props.mergeValues),
      onSelectionUpdate: props.hasContextualCommenting ? handleSelectionUpdate : () => undefined,
      editorProps: {
        handleClickOn: (view, position, _n, _np, _e, direct) => {
          if (!direct) {
            return false;
          }

          const commentId = view.state.doc
            .resolve(position)
            .marks()
            .find(mark => mark.type.name === 'commentHighlight')?.attrs.commentId;

          if (commentId) {
            emit('select-comment', commentId);
          }

          return false;
        },
      },
    }));

    const commentButtonPosition: Ref<{ top: number; left: number }> = ref(null);

    const renderComments = (comments: Comment[]) => {
      if (!props.hasContextualCommenting) {
        return;
      }

      const commentsInNode = comments.filter(
        comment => comment.editorSelection.nodeId === props.nodeId
      );

      const selectedComment = commentsInNode.find(
        comment => comment.id === props.selectedCommentId
      );

      // Selected comment mark is rendered last, on top of other comment marks
      const commentsToRender = selectedComment
        ? [
            ...commentsInNode.filter(comment => comment.id !== props.selectedCommentId),
            { ...selectedComment, status: 'Selected' },
          ]
        : commentsInNode;

      viewer.value.commands.selectAll();
      viewer.value.commands.unsetCommentHighlight();

      for (const comment of commentsToRender) {
        const { anchor, head } = comment.editorSelection.textSelection;
        const [from, to] = [anchor, head].sort();

        viewer.value.commands.setTextSelection({ from, to });
        viewer.value.commands.setCommentHighlight({
          commentState: comment.status,
          commentId: comment.id,
        });
      }
    };

    watch(
      () => [props.comments, props.selectedCommentId],
      () => {
        renderComments(props.comments);
      }
    );

    const updateCommentButtonPosition = (top: number, left: number) => {
      commentButtonPosition.value = { top, left };
    };

    const debounceUpdateCommentButtonPosition = debounce(updateCommentButtonPosition, 300);

    const handleSelectionUpdate = ({
      editor,
    }: {
      editor: TipTapEditor;
      transaction: Transaction;
    }) => {
      commentButtonPosition.value = null;
      const { from, to } = editor.view.state.selection;
      if (from === to) {
        return;
      }
      const rightMostMarkerPosition = editor.view.coordsAtPos(to);
      const { bottom, left } = rightMostMarkerPosition;
      const editorOffset = editorContainer.value.getBoundingClientRect();
      const offset = 60;

      debounceUpdateCommentButtonPosition(
        bottom - editorOffset.top - offset,
        left - editorOffset.left
      );
    };

    const createComment = () => {
      commentButtonPosition.value = null;
      const { selection } = viewer.value.view.state;
      const { head, anchor } = selection.toJSON();
      const editor = viewer.value;
      if (!editor) return;

      const content = JSON.stringify(
        JSON.stringify(editor.view.state.doc.cut(selection.from, selection.to).toJSON())
      );

      const textSelection: ProseMirrorSelection = {
        head,
        anchor,
        type: SelectionType.Text,
      };
      emit('create-comment', {
        textSelection,
        content,
      });
    };

    onMounted(() => {
      viewer.value = new TipTapEditor(textViewer.value);
      renderComments(props.comments);
    });

    onBeforeUnmount(() => {
      viewer.value.destroy();
    });

    watch(
      () => [props.fields, props.mergeValues],
      () => {
        updateMergeValues(contentPayload.value);
      }
    );

    watch(
      () => props.content,
      () => {
        setContent(contentPayload.value);
      }
    );

    return {
      createComment,
      commentButtonPosition,
      viewer,
      editorContainer,
    };
  },
});
